import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import {
  MARK_VIEWBOX,
  RING_PATH,
  RING_STROKE_WIDTH,
  RING_LEN,
  STAR_PATH,
  STAR_TRANSFORM,
  STAR_SCALE,
} from './markGeometry';

/**
 * THE size scale - named for the job, not for a t-shirt.
 *
 * The px values are not invented: they are what call sites were already
 * forcing with `!h-*`/`!w-*` overrides, counted and collapsed into six
 * intents. If a caller still reaches for an override, the scale is missing a
 * step - add it here rather than at the call site.
 *
 *   inline (12) - inside a line of text / a dense status row
 *   glyph  (16) - icon slot: a button's icon, a list-row leading glyph
 *   sm     (24) - inside a control (button body, small tile)
 *   md     (32) - a card or pane's own loading moment
 *   lg     (48) - a page section / modal body
 *   xl     (96) - fullscreen boot, brand moments
 */
export type BrandLoaderSizeName = 'inline' | 'glyph' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * @deprecated The old t-shirt scale. `xs` maps to `glyph`; the rest keep
 * their pixels and their names. Kept so the existing call sites compile —
 * lane migrations replace them with intent names.
 */
export type BrandLoaderLegacySize = 'xs';

export type BrandLoaderSize = BrandLoaderSizeName | BrandLoaderLegacySize;

/** Named intent → px. The single source for the scale. */
export const BRAND_LOADER_PX: Record<BrandLoaderSizeName, number> = {
  inline: 12,
  glyph: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 96,
};

/** Deprecated alias → nearest intent name. */
const LEGACY_SIZE_ALIAS: Record<BrandLoaderLegacySize, BrandLoaderSizeName> = {
  xs: 'glyph', // 16px, unchanged
};

const SIZE_CLASS: Record<BrandLoaderSizeName, string> = {
  inline: 'w-3 h-3',
  glyph: 'w-4 h-4',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-24 h-24',
};

const resolveSize = (size: BrandLoaderSize): BrandLoaderSizeName =>
  (LEGACY_SIZE_ALIAS as Record<string, BrandLoaderSizeName>)[size] ?? (size as BrandLoaderSizeName);

/** Ring colour. `current` inherits from the parent (buttons, badges, pills). */
export type BrandLoaderTone = 'brand' | 'current';

const TONE_CLASS: Record<BrandLoaderTone, string> = {
  brand: 'text-fg-strong',
  current: 'text-current',
};

export interface BrandLoaderProps {
  size?: BrandLoaderSize;
  /**
   * `brand` (default) draws the ring in the strong foreground token;
   * `current` inherits `color` from the parent - use it inside a button,
   * badge or pill instead of an `!text-current` escape.
   */
  tone?: BrandLoaderTone;
  /** Visible caption under the loader. Pass an already-translated string. */
  label?: string;
  /** Wrap in a flex-centered container */
  centered?: boolean;
  /** Fill the viewport on the plain brand background (app boot / route gates) */
  fullscreen?: boolean;
  className?: string;
}

/**
 * THE loading indicator for PONS - the brand ring-draw animation
 * (stroke draws on, un-draws, champagne star pops). Use this for AI
 * processes and any brand loading moment, always on a plain background:
 * no dark overlays, no card chrome. For content-shaped loading use
 * Skeleton; for in-button pending state use Button's isLoading.
 *
 * Theme-aware via currentColor (ink on light, paper on dark). When the
 * org runs white-label, the champagne star is dropped (ring only) so
 * the animation stays brand-neutral.
 *
 * SIZE BY INTENT, NEVER BY OVERRIDE. `inline | glyph | sm | md | lg | xl`
 * cover every size the app was reaching for with `!h-*`/`!w-*`, and `tone`
 * covers what `!text-current` was for:
 *
 *     <BrandLoader size="glyph" tone="current" />   // inside a button
 *     <BrandLoader size="xl" fullscreen />          // app boot
 */
const BrandLoader: React.FC<BrandLoaderProps> = ({
  size = 'md',
  tone = 'brand',
  label,
  centered = false,
  fullscreen = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const preferences = useSelector((state: RootState) => state.lists?.preferences);
  const isWhiteLabelEnabled =
    preferences?.WhiteLableEnabled === true || preferences?.WhiteLableEnabled === '1';

  const spinner = (
    <div
      role="status"
      aria-live="polite"
      aria-label={label || t('Loading')}
      className="flex flex-col items-center gap-3"
    >
      <svg
        viewBox={MARK_VIEWBOX}
        className={`${SIZE_CLASS[resolveSize(size)]} ${TONE_CLASS[tone]} ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d={RING_PATH}
          fill="none"
          stroke="currentColor"
          strokeWidth={RING_STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={RING_LEN}
        >
          <animate
            attributeName="stroke-dashoffset"
            values={`${RING_LEN};${RING_LEN};0;0;-${RING_LEN};-${RING_LEN}`}
            keyTimes="0;0.05;0.4;0.46;0.68;1"
            calcMode="spline"
            keySplines="0 0 1 1;0.45 0 0.3 1;0 0 1 1;0.45 0 0.35 1;0 0 1 1"
            dur="3.6s"
            repeatCount="indefinite"
          />
        </path>
        {!isWhiteLabelEnabled && (
          <g transform={STAR_TRANSFORM}>
            <g>
              <animateTransform
                attributeName="transform"
                type="scale"
                values="1;1;1.3;1;1"
                keyTimes="0;0.74;0.82;0.9;1"
                calcMode="spline"
                keySplines="0 0 1 1;0.4 0 0.3 1;0.4 0 0.3 1;0 0 1 1"
                dur="3.6s"
                repeatCount="indefinite"
                additive="sum"
              />
              <path d={STAR_PATH} transform={STAR_SCALE} fill="rgb(var(--champagne))" />
            </g>
          </g>
        )}
      </svg>
      {label && <span className="text-sm text-fg-muted">{label}</span>}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-bg">
        {spinner}
      </div>
    );
  }

  if (centered) {
    return <div className="flex items-center justify-center">{spinner}</div>;
  }

  return spinner;
};

export default BrandLoader;
